import { Fragment, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useShop } from "@/contexts/ShopContext";
import {
  ClipboardList, Flame, ArrowUpRight, Wallet, Clock, Bell, PackageCheck,
  ShoppingBag, Package, Globe,
} from "lucide-react";

const CHANNEL_ICONS = { LiveCounter: ShoppingBag, Takeaway: Package, Online: Globe };

const STAT_CARDS = [
  { key: "orders_today", label: "Orders today", icon: ClipboardList, tint: "var(--teal-soft)", fg: "var(--teal)", to: "/orders/status", note: "Total tickets opened today" },
  { key: "in_kitchen", label: "In the kitchen", icon: Flame, tint: "var(--orange-soft)", fg: "var(--orange)", to: "/kitchen", note: "Orders still being prepared" },
  { key: "ready_count", label: "Ready to handoff", icon: ArrowUpRight, tint: "var(--amber-soft)", fg: "var(--amber-fg)", to: "/delivery", note: "Waiting for pickup or delivery" },
  { key: "revenue_today", label: "Revenue today", icon: Wallet, tint: "var(--green-soft)", fg: "var(--green)", to: "/reports", prefix: "₹", note: "Paid orders so far today" },
];

const PIPELINE_STEPS = [
  { key: "pending", label: "Pending", icon: Clock, tint: "var(--amber-soft)", fg: "var(--amber-fg)" },
  { key: "preparing", label: "Preparing", icon: Flame, tint: "var(--orange-soft)", fg: "var(--orange)" },
  { key: "ready", label: "Ready", icon: Bell, tint: "var(--blue-soft)", fg: "var(--blue)" },
  { key: "delivered", label: "Delivered", icon: PackageCheck, tint: "var(--green-soft)", fg: "var(--green)" },
];

export default function Overview() {
  const { user } = useAuth();
  const { currentShopId } = useShop();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!currentShopId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard", { params: { shop_id: currentShopId } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [currentShopId]);

  useEffect(() => { load(); }, [load]);

  if (!currentShopId) {
    return <div className="card" style={{ padding: 20 }} data-testid="no-shop-message">No shop assigned yet.</div>;
  }
  if (loading || !data) {
    return <div data-testid="overview-loading">Loading…</div>;
  }

  const maxPulse = Math.max(1, ...data.kitchen_pulse.map((p) => p.count));
  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <section className="view" data-testid="overview-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Kitchen Operations · Overview</div>
          <h1>Good day, {firstName}.</h1>
          <p>Monitor today's orders, kitchen activity and handoffs from one place.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/orders/new")} data-testid="new-order-btn">＋ New order</button>
      </div>

      <div className="stats">
        {STAT_CARDS.map((c) => (
          <div className="card stat" key={c.key} data-testid={`stat-card-${c.key}`}>
            <div className="stat-decor" style={{ "--stat-tint": c.tint }} />
            <div className="stat-top">
              <div className="stat-icon" style={{ background: c.tint, color: c.fg }}><c.icon size={18} /></div>
              <button className="stat-expand" onClick={() => navigate(c.to)} aria-label={`Open ${c.label}`} data-testid={`stat-expand-${c.key}`}>
                <ArrowUpRight size={13} />
              </button>
            </div>
            <div className="stat-body">
              <div className="stat-value">{c.prefix || ""}{c.key === "revenue_today" ? data[c.key].toFixed(0) : data[c.key]}</div>
              <div className="stat-label">{c.label}</div>
              <div className="stat-note">{c.note}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="page-head" style={{ marginBottom: 12 }}>
        <div>
          <div className="eyebrow">Live operations</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Today's activity</h2>
        </div>
        <span className="status delivered">System live</span>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-head">
          <div><h2>Order pipeline</h2><p>Where today's orders currently sit</p></div>
          <button className="link" onClick={() => navigate("/orders/status")}>View all →</button>
        </div>
        <div className="pipeline" data-testid="order-pipeline">
          {PIPELINE_STEPS.map((s, idx) => (
            <Fragment key={s.key}>
              <div className="pipeline-node" data-testid={`pipeline-${s.key}`}>
                <div className="pipeline-icon" style={{ "--stat-tint": s.tint, "--node-fg": s.fg }}>{data.pipeline[s.key]}</div>
                <div className="pipeline-label">{s.label}</div>
              </div>
              {idx < PIPELINE_STEPS.length - 1 && <div className="pipeline-line" />}
            </Fragment>
          ))}
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="section-head">
            <div><h2>Recent orders</h2><p>The last few tickets through the pass</p></div>
            <button className="link" onClick={() => navigate("/orders/status")}>Order status →</button>
          </div>
          {data.recent_orders.length === 0 && <div className="empty" style={{ margin: 16 }}>No orders yet</div>}
          {data.recent_orders.map((o) => (
            <div className="order-row" key={o.id} data-testid={`recent-order-${o.order_number}`}>
              <div className="order-number mono">#{o.order_number}</div>
              <div className="order-main">
                <strong>{o.customer_name}</strong>
                <div className="order-meta">{o.order_type} · {o.lines.length} items</div>
              </div>
              <span className={`status ${o.status}`}>{o.status.replace("_", " ")}</span>
              <div className="order-total mono">₹{o.total}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="section-head">
            <div><h2>Where orders come from</h2><p>Channel mix today</p></div>
          </div>
          <div style={{ padding: "6px 0" }}>
            {data.channel_mix.map((c) => {
              const Icon = CHANNEL_ICONS[c.type] || ShoppingBag;
              return (
                <div className="channel-row" key={c.type} data-testid={`channel-${c.type}`}>
                  <div className="channel-icon"><Icon size={14} /></div>
                  <div className="channel-info">
                    <div className="channel-top"><span>{c.label}</span><span>{c.count} orders</span></div>
                    <div className="channel-bar"><i style={{ width: `${c.pct}%` }} /></div>
                  </div>
                  <div className="channel-pct">{c.pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="section-head">
          <div><h2>Kitchen demand</h2><p>Items currently waiting to be prepared</p></div>
          <button className="link" onClick={() => navigate("/kitchen")}>Open kitchen →</button>
        </div>
        <div className="pulse">
          {data.kitchen_pulse.length === 0 && <div className="empty">Nothing cooking right now</div>}
          {data.kitchen_pulse.map((p) => (
            <div className="pulse-item" key={p.name}>
              <div className="pulse-line"><strong>{p.name}</strong><span>{p.count} to make</span></div>
              <div className="bar teal"><i style={{ width: `${(p.count / maxPulse) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
